/**
 * Balance de Situación.
 *
 * Selector de fecha "a" + opción de comparar con periodo anterior. Tabla con
 * tres grupos (Activo / Pasivo / Patrimonio Neto) mostrando saldos acumulados.
 * Alerta si activo ≠ pasivo + PN (diff > 1 céntimo).
 *
 * La API deja leerlo a ADMIN y CONTABILIDAD. Debajo de la fecha, los ratios de balance y la
 * estructura del balance a esa misma fecha.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  AlertTriangle,
  Calculator,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { downloadFile } from '../lib/download'
import { formatCents } from '../lib/money'
import type { BalanceReport, BalanceSection } from '@jr2os/shared'
import BalanceRatiosAndStructure from '../components/accounting/BalanceRatiosAndStructure'

/** Hoy en formato YYYY-MM-DD. */
function todayStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Resta un año a YYYY-MM-DD. */
function shiftYear(s: string, delta: number): string {
  const [y, m, d] = s.split('-').map(Number)
  if (!y || !m || !d) return s
  return `${y + delta}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

const ACTIVE_GROUP_ORDER: BalanceSection['key'][] = ['ACTIVO_NO_CORRIENTE', 'ACTIVO_CORRIENTE']
const PASSIVE_GROUP_ORDER: BalanceSection['key'][] = [
  'PASIVO_NO_CORRIENTE',
  'PASIVO_CORRIENTE',
  'PATRIMONIO_NETO',
]

export default function AccountingBalance() {
  const { t } = useTranslation('accounting')
  const [at, setAt] = useState<string>(todayStr())
  const [compare, setCompare] = useState<boolean>(true)
  const [report, setReport] = useState<BalanceReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const previousAt = useMemo(() => (compare ? shiftYear(at, -1) : null), [at, compare])
  /** Ejercicio de la fecha del balance, para el enlace al Mayor de cada cuenta. */
  const balanceYear = Number(at.slice(0, 4)) || new Date().getFullYear()

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ at })
      if (previousAt) params.set('previousAt', previousAt)
      const data = await api.get<BalanceReport>(`/accounting/reports/balance?${params.toString()}`)
      setReport(data)
    } catch (err) {
      const text =
        err instanceof ApiError
          ? ((err.body as { error?: string })?.error ?? err.message)
          : err instanceof Error
            ? err.message
            : t('balance.errorLoad')
      setError(text)
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [at, previousAt, t])

  useEffect(() => {
    void load()
  }, [load])

  const activeSections = useMemo(
    () =>
      report
        ? ACTIVE_GROUP_ORDER.map((k) => report.sections.find((s) => s.key === k)).filter(
            (s): s is BalanceSection => Boolean(s)
          )
        : [],
    [report]
  )
  const passiveSections = useMemo(
    () =>
      report
        ? PASSIVE_GROUP_ORDER.map((k) => report.sections.find((s) => s.key === k)).filter(
            (s): s is BalanceSection => Boolean(s)
          )
        : [],
    [report]
  )

  const downloadPdf = async () => {
    const params = new URLSearchParams({ at })
    if (previousAt) params.set('previousAt', previousAt)
    try {
      await downloadFile(
        `/accounting/reports/balance/pdf?${params}`,
        `balance-de-situacion-${at}.pdf`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : t('balance.errorLoad'))
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center gap-3">
        <div className="p-2 rounded-lg bg-jr2-500/15 text-jr2-700 dark:text-jr2-300">
          <Calculator size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-semibold">{t('balance.pageTitle')}</h1>
          <p className="text-sm t-muted">{t('balance.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => void downloadPdf()}
          className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary flex items-center gap-1.5"
        >
          <FileText size={14} /> {t('balance.pdf')}
        </button>
      </header>

      {/* Controles */}
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
        <div>
          <label className="block text-xs font-medium t-muted mb-1">{t('balance.atDate')}</label>
          <input
            type="date"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            className="rounded-md border border-[var(--border)] bg-[var(--bg-input)] t-primary px-3 py-2 text-sm"
          />
        </div>
        <label className="flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
            className="accent-jr2-500"
          />
          {t('balance.compareWithPreviousYear')}{' '}
          {previousAt && <span className="t-muted">({previousAt})</span>}
        </label>
      </div>

      {/* Los ratios de balance y la estructura, A LA MISMA FECHA que la tabla. */}
      <BalanceRatiosAndStructure at={at} />

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm t-muted">
          <Loader2 size={16} className="animate-spin" />
          {t('balance.loading')}
        </div>
      )}

      {report && !loading && (
        <>
          {/* Alerta de cuadre */}
          {report.balanced ? (
            <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 size={16} />
              {t('balance.balanced')}
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle size={16} />
              {t('balance.notBalanced', { diff: formatCents(report.difference) })}
            </div>
          )}

          {/* Si hay cuentas fuera de todo bloque, se NOMBRAN: el descuadre deja
              de ser un misterio y pasa a ser una lista accionable. */}
          {(report.unclassifiedAccounts?.length ?? 0) > 0 && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle size={16} />
                {t('balance.unclassified')}
              </div>
              <p className="text-xs mt-1 font-mono">{report.unclassifiedAccounts.join(' · ')}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BalanceTable
              title={t('balance.assets')}
              sections={activeSections}
              total={report.totalActivo}
              showPrevious={Boolean(previousAt)}
              year={balanceYear}
            />
            <BalanceTable
              title={t('balance.liabilitiesAndEquity')}
              sections={passiveSections}
              total={report.totalPasivoPatrimonio}
              showPrevious={Boolean(previousAt)}
              year={balanceYear}
            />
          </div>
        </>
      )}
    </div>
  )
}

function BalanceTable({
  title,
  sections,
  total,
  showPrevious,
  year,
}: {
  title: string
  sections: BalanceSection[]
  total: number
  showPrevious: boolean
  year: number
}) {
  const { t } = useTranslation('accounting')
  return (
    <div className="overflow-hidden rounded-xl border border-[var(--border)]">
      <div className="px-4 py-3 bg-[var(--bg-input-soft)] border-b border-[var(--border)]">
        <h2 className="font-semibold t-primary">{title}</h2>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[var(--bg-input-soft)] t-muted">
          <tr>
            <th className="text-left px-4 py-2 font-medium">{t('balance.concept')}</th>
            <th className="text-right px-4 py-2 font-medium">{t('balance.amount')}</th>
            {showPrevious && (
              <th className="text-right px-4 py-2 font-medium">{t('balance.previousPeriod')}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {sections.map((sec) => (
            <SectionBlock key={sec.key} section={sec} showPrevious={showPrevious} year={year} />
          ))}
        </tbody>
        <tfoot className="bg-[var(--bg-hover)] border-t border-[var(--border)]">
          <tr>
            <td className="px-4 py-2 font-semibold t-primary">
              {t('balance.total', { name: title.toLowerCase() })}
            </td>
            <td className="px-4 py-2 text-right font-mono font-semibold t-primary">
              {formatCents(total)}
            </td>
            {showPrevious && <td className="px-4 py-2 text-right font-mono t-muted">—</td>}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function SectionBlock({
  section,
  showPrevious,
  year,
}: {
  section: BalanceSection
  showPrevious: boolean
  /** Ejercicio de la fecha del balance: viaja al Mayor en cada cuenta. */
  year: number
}) {
  const { t } = useTranslation(['accounting'])
  // Plegado por defecto, como en Ledgeria: el balance se lee por masas y el
  // desglose se abre cuando hace falta.
  const [open, setOpen] = useState(false)
  return (
    <>
      <tr className="bg-[var(--bg-input-soft)]/60 border-t border-[var(--border)]">
        <td className="px-4 py-2 font-medium t-primary">
          {section.accounts.length === 0 ? (
            section.title
          ) : (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 text-left hover:underline"
              aria-expanded={open}
            >
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              {section.title}
              <span className="t-faint text-[11px] font-normal">({section.accounts.length})</span>
            </button>
          )}
        </td>
        <td className="px-4 py-2 text-right font-mono font-medium t-primary">
          {formatCents(section.subtotal)}
        </td>
        {showPrevious && (
          <td className="px-4 py-2 text-right font-mono t-muted">
            {section.previousSubtotal !== null ? formatCents(section.previousSubtotal) : '—'}
          </td>
        )}
      </tr>
      {open &&
        section.accounts.map((a) => (
          <tr key={`${section.key}-${a.code}`} className="border-t border-[var(--border)]/60">
            <td className="px-4 py-1.5 pl-10 t-secondary">
              {/* De la masa al movimiento: el Mayor de esa cuenta. */}
              <Link
                to={`/accounting/ledger?accountCode=${encodeURIComponent(a.code)}&year=${year}`}
                className="hover:underline"
                title={t('openLedger')}
              >
                <span className="t-muted mr-2 font-mono">{a.code}</span>
                {a.name}
              </Link>
            </td>
            <td className="px-4 py-1.5 text-right font-mono t-secondary">
              {formatCents(a.amount)}
            </td>
            {showPrevious && (
              <td className="px-4 py-1.5 text-right font-mono t-muted">
                {a.previousAmount !== null ? formatCents(a.previousAmount) : '—'}
              </td>
            )}
          </tr>
        ))}
    </>
  )
}
